import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { trackEvent } from '@/lib/analytics';
import type { Session, User, AuthError } from '@supabase/supabase-js';
import type { Profile, GameState, UserSettings } from '@/types/database';

/* ─────────────────────────────────────────────────────────────────
   AUTH STORE — Zustand-based authentication state management
   
   Manages: Session, User, Profile, Settings, GameState
   Handles: Login (Google, GitHub, Email magic link), Logout, 
            Profile hydration on session change
   ───────────────────────────────────────────────────────────────── */

interface AuthState {
  // Core auth
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  settings: UserSettings | null;
  gameState: GameState | null;

  // UI state
  isLoading: boolean;
  isHydrated: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithGitHub: () => Promise<void>;
  signInWithEmail: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  hydrateProfile: (userId: string) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  session: null,
  user: null,
  profile: null,
  settings: null,
  gameState: null,
  isLoading: true,
  isHydrated: false,
  error: null,

  clearError: () => set({ error: null }),

  /**
   * Initialize auth — called once on app mount.
   * Gets the current session, sets up the auth state listener,
   * and hydrates profile data if a session exists.
   */
  initialize: async () => {
    try {
      // Get current session
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;

      if (session?.user) {
        set({ session, user: session.user });
        await get().hydrateProfile(session.user.id);
      }

      // Listen for auth changes (login, logout, token refresh)
      supabase.auth.onAuthStateChange(async (event, newSession) => {
        set({ session: newSession, user: newSession?.user ?? null });

        if (event === 'SIGNED_IN' && newSession?.user) {
          const created = new Date(newSession.user.created_at).getTime();
          if (Date.now() - created < 300_000) {
            const key = `johnicorn:signup_tracked_${newSession.user.id}`;
            if (!window.localStorage.getItem(key)) {
              window.localStorage.setItem(key, '1');
              trackEvent('signup');
            }
          }
        }

        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && newSession?.user) {
          await get().hydrateProfile(newSession.user.id);
        }

        if (event === 'SIGNED_OUT') {
          set({
            profile: null,
            settings: null,
            gameState: null,
          });
        }
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to initialize auth';
      set({ error: msg });
    } finally {
      set({ isLoading: false, isHydrated: true });
    }
  },

  /**
   * Hydrate profile, settings, and game_state from Supabase.
   * Called after sign-in and on app mount if session exists.
   */
  hydrateProfile: async (userId: string) => {
    try {
      // We can now use the correctly typed Supabase client
      const client = supabase;

      const [profileRes, settingsRes, gameStateRes] = await Promise.all([
        client.from('profiles').select('*').eq('id', userId).single(),
        client.from('user_settings').select('*').eq('user_id', userId).single(),
        client.from('game_state').select('*').eq('user_id', userId).single(),
      ]);

      set({
        profile: profileRes.data ?? null,
        settings: settingsRes.data ?? null,
        gameState: gameStateRes.data ?? null,
      });
    } catch (err) {
      console.error('Failed to hydrate profile:', err);
    }
  },

  /**
   * Sign in with Google OAuth.
   */
  signInWithGoogle: async () => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (err) {
      const msg = (err as AuthError).message ?? 'Google sign-in failed';
      set({ error: msg, isLoading: false });
    }
  },

  /**
   * Sign in with GitHub OAuth.
   */
  signInWithGitHub: async () => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (err) {
      const msg = (err as AuthError).message ?? 'GitHub sign-in failed';
      set({ error: msg, isLoading: false });
    }
  },

  /**
   * Sign in with Email magic link (passwordless).
   */
  signInWithEmail: async (email: string) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
      set({ isLoading: false });
    } catch (err) {
      const msg = (err as AuthError).message ?? 'Email sign-in failed';
      set({ error: msg, isLoading: false });
    }
  },

  /**
   * Sign out — clears session and all user data.
   */
  signOut: async () => {
    set({ isLoading: true });
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      set({
        session: null,
        user: null,
        profile: null,
        settings: null,
        gameState: null,
        isLoading: false,
      });
    } catch (err) {
      const msg = (err as AuthError).message ?? 'Sign-out failed';
      set({ error: msg, isLoading: false });
    }
  },
}));
